import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { DataContext } from '../../../context/DataContext';

import { getRol, getToken, getUser } from '../../../api/Account';
import { getCobroDiarioReportAsync } from '../../../api/Report';
import { getGestoresAsync } from '../../../api/Users';
import { simpleMessage } from '../../../helpers/Helpers';
import { formatearFecha, formatearMonto } from '../../../helpers/formato';
import ReporteAgrupado from '../../../components/reportes/ReporteAgrupado';

const TODOS = 't';

// Un crédito por fila: sus montos se suman directo.
const dinero = (key, label, total = false) => ({
	key,
	label,
	align: 'center',
	total,
	formatoTotal: formatearMonto,
	valor: (c) => formatearMonto(c[key]),
	excel: (c) => c[key]
});

const fecha = (key, label) => ({
	key,
	label,
	align: 'center',
	valor: (c) => formatearFecha(c[key]),
	excel: (c) => formatearFecha(c[key])
});

const numero = (key, label) => ({
	key,
	label,
	align: 'center',
	valor: (c) => c[key],
	excel: (c) => c[key]
});

// Solo se totalizan Cuota y Saldo Total (los "Total Cuotas Día" y "Total Saldo" de siempre).
// El Total Atraso del reporte cuenta solo los créditos en atraso (tipo M), así que no es
// la suma de esta columna: va en el resumen.
const COLUMNAS = [
	{ key: 'fullName', label: 'Cliente', align: 'left', texto: true, valor: (c) => c.fullName },
	{ key: 'address', label: 'Dirección', align: 'left', texto: true, valor: (c) => c.address },
	{ key: 'phone', label: 'Teléfono', align: 'center', texto: true, valor: (c) => c.phone },
	fecha('fechaEntrega', 'F.Entrega'),
	fecha('fechaVencimiento', 'F.Vencimiento'),
	{ key: 'tipo', label: 'Tipo', align: 'center', texto: true, valor: (c) => c.tipo },
	dinero('cuota', 'Cuota', true),
	dinero('atraso', 'Atraso'),
	numero('atrasoDias', 'A.Dias'),
	numero('cuotasAtrasadas', 'A.Cuotas'),
	dinero('cuotaMasAtraso', 'C + A'),
	dinero('saldoTotal', 'Saldo Total', true)
];

// "x%" del saldo total; con saldo 0 no hay porcentaje que dar.
const porcentaje = (parte, saldo) => (saldo > 0 ? `${((parte / saldo) * 100).toFixed(2)}%` : '—');

// Mismas fórmulas de siempre: conteos por tipo (D día, M en atraso, V vencido), Total
// Atraso solo de M y Total Vencido como el saldo total de los V, ambos sobre el saldo total.
const resumen = (filas) => {
	const delTipo = (tipo) => filas.filter((c) => c.tipo === tipo);
	const suma = (lista, campo) => lista.reduce((total, c) => total + c[campo], 0);
	const saldo = suma(filas, 'saldoTotal');
	const atraso = suma(delTipo('M'), 'atraso');
	const vencido = suma(delTipo('V'), 'saldoTotal');

	return [
		{ label: 'Créditos del Día', valor: delTipo('D').length },
		{ label: 'Créditos en Atraso', valor: delTipo('M').length },
		{
			label: 'Total Atraso',
			valor: `${formatearMonto(atraso)} = ${porcentaje(atraso, saldo)}`
		},
		{ label: 'Créditos Vencidos', valor: delTipo('V').length },
		{
			label: 'Total Vencido',
			valor: `${formatearMonto(vencido)} = ${porcentaje(vencido, saldo)}`
		}
	];
};

const CobroDiario = () => {
	const [data, setData] = useState([]);
	const [gestorName, setGestorName] = useState('Todos');

	const { selectedGestor, selectedFecha } = useParams();
	const { setIsLoading, setIsUnautorized, title, slogan, icon } = useContext(DataContext);

	const token = getToken();
	const user = getUser();
	const rol = getRol();

	const manejarError = useCallback(
		(result) => {
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
		},
		[setIsUnautorized]
	);

	useEffect(() => {
		(async () => {
			setIsLoading(true);
			const [reporte, gestores] = await Promise.all([
				getCobroDiarioReportAsync(token, {
					fecha: selectedFecha.toString(),
					gestorId: selectedGestor === TODOS ? 0 : selectedGestor
				}),
				selectedGestor === TODOS ? null : getGestoresAsync(token)
			]);
			setIsLoading(false);

			if (!reporte.statusResponse) {
				manejarError(reporte);
				return;
			}
			setData(reporte.data.result);

			if (selectedGestor !== TODOS) {
				// Si la lista de gestores falla o no lo trae (dado de baja), el nombre sale del reporte.
				const gestor =
					gestores && gestores.statusResponse
						? gestores.data.result.find((g) => g.id.toString() === selectedGestor)
						: null;
				setGestorName(
					gestor ? gestor.fullName : reporte.data.result[0]?.gestorNombre ?? selectedGestor
				);
			}
		})();
	}, [token, selectedGestor, selectedFecha, setIsLoading, manejarError]);

	// C + A no viene del servidor: se arma aquí para poder ordenar por esa columna.
	const filas = useMemo(
		() => data.map((c) => ({ ...c, cuotaMasAtraso: c.cuota + c.atraso })),
		[data]
	);

	// El servidor manda el nombre del gestor del crédito: también el de uno dado de baja.
	const agruparPor = useCallback((fila) => fila.gestorNombre, []);

	return (
		<ReporteAgrupado
			tituloReporte="Reporte de Cobro Diario"
			nombreArchivo="CobroDiario"
			parametros={[
				{ label: 'Gestor Seleccionado', valor: gestorName },
				{ label: 'Fecha de Cobro', valor: formatearFecha(selectedFecha) }
			]}
			columnas={COLUMNAS}
			data={filas}
			agruparPor={agruparPor}
			resumen={resumen}
			ordenInicial="tipo"
			contexto={{ title, slogan, icon, user, rol }}
		/>
	);
};

export default CobroDiario;
